/**
 * MA-PPB-CONSENT-HEADER-078: Shared Header Icon System
 * 
 * This is the CANONICAL icon map used by BOTH:
 * - FunnelLivePreview (PPB Funnel Step)
 * - ConversationFunnel (PPB Landing Page Step / Runtime)
 * 
 * DO NOT duplicate or modify inconsistently across surfaces.
 */

import {
  Activity,
  Award,
  BarChart,
  Briefcase,
  Calendar,
  CheckCircle,
  Clipboard,
  Compass,
  Feather,
  FileText,
  Gift,
  Heart,
  HelpCircle,
  Home,  // MA-260 P0: Added missing "home" icon
  Layers,
  Lock,
  MessageCircle,
  Rocket,
  Search,
  Shield,
  Sparkles,
  Star,
  TrendingUp,
  Umbrella,
  Wallet,
  Zap,
} from "lucide-react";

/**
 * MA-264: Supported Header Icons (Single Source of Truth)
 * 
 * This array defines the ONLY icons that are:
 * 1. Available in the icon picker UI
 * 2. Supported by all renderers (Builder, PPB, LP, Runtime)
 * 3. Validated by the backend
 * 
 * Adding a new icon requires:
 * - Import the icon component from lucide-react (above)
 * - Add to this array with name, component, and label
 * - Icon picker and backend validation will auto-sync
 */
export const SUPPORTED_HEADER_ICONS = [
  { name: "check-circle", component: CheckCircle, label: "Check" },
  { name: "clipboard", component: Clipboard, label: "Clipboard" },
  { name: "search", component: Search, label: "Search" },
  { name: "trending-up", component: TrendingUp, label: "Growth" },
  { name: "bar-chart", component: BarChart, label: "Chart" },
  { name: "activity", component: Activity, label: "Activity" },
  { name: "shield", component: Shield, label: "Shield" },
  { name: "home", component: Home, label: "Home" },
  { name: "lock", component: Lock, label: "Lock" },
  { name: "umbrella", component: Umbrella, label: "Umbrella" },
  { name: "calendar", component: Calendar, label: "Calendar" },
  { name: "wallet", component: Wallet, label: "Wallet" },
  { name: "briefcase", component: Briefcase, label: "Briefcase" },
  { name: "file-text", component: FileText, label: "Document" },
  { name: "sparkles", component: Sparkles, label: "Sparkles" },
  { name: "compass", component: Compass, label: "Compass" },
  { name: "zap", component: Zap, label: "Zap" },
  { name: "rocket", component: Rocket, label: "Rocket" },
  { name: "help-circle", component: HelpCircle, label: "Help" },
  { name: "message-circle", component: MessageCircle, label: "Message" },
  { name: "gift", component: Gift, label: "Gift" },
  { name: "award", component: Award, label: "Award" },
  { name: "star", component: Star, label: "Star" },
  { name: "heart", component: Heart, label: "Heart" },
  { name: "feather", component: Feather, label: "Feather" },
  { name: "layers", component: Layers, label: "Layers" },
];

/**
 * Canonical mapping of icon_type tokens to Lucide React components
 * 
 * MA-264: Derived from SUPPORTED_HEADER_ICONS for consistency
 * These tokens MUST match the icon list in:
 * /app/backend/app/services/ai_builder.py (lines 83-95)
 */
export const HEADER_ICON_MAP = {
  "check-circle": CheckCircle,
  "clipboard": Clipboard,
  "search": Search,
  "trending-up": TrendingUp,
  "bar-chart": BarChart,
  "activity": Activity,
  "shield": Shield,
  "home": Home,
  "lock": Lock,
  "umbrella": Umbrella,
  "calendar": Calendar,
  "wallet": Wallet,
  "briefcase": Briefcase,
  "file-text": FileText,
  "sparkles": Sparkles,
  "compass": Compass,
  "zap": Zap,
  "rocket": Rocket,
  "help-circle": HelpCircle,
  "message-circle": MessageCircle,
  "gift": Gift,
  "award": Award,
  "star": Star,
  "heart": Heart,
  "feather": Feather,
  "layers": Layers,
};

/**
 * Default fallback icon when icon_type is missing or invalid
 */
export const DEFAULT_HEADER_ICON = "shield";

/**
 * Shared icon resolver used by BOTH surfaces
 * MA-234: STRICT CANONICAL READ - Only uses header.icon_type
 * 
 * Resolution priority (MA-234):
 * 1. header.icon_type (CANONICAL - ONLY SOURCE)
 * 2. "shield" (default if missing)
 * 
 * Legacy fallbacks REMOVED: header.selectedIcon, header.icon
 * 
 * @param {Object} header - Header object from funnel data
 * @returns {React.Component} Lucide icon component
 */
export const resolveHeaderIcon = (header) => {
  // MA-234: STRICT canonical field only
  const iconType = header?.icon_type ?? DEFAULT_HEADER_ICON;
  
  // MA-235: RUNTIME DEBUG
  console.log('[MA-235 AUDIT] resolveHeaderIcon called:', {
    'header_input': header,
    'iconType_resolved': iconType,
    'DEFAULT_HEADER_ICON': DEFAULT_HEADER_ICON,
  });

  // Map "default" to shield
  if (iconType === "default") {
    console.log('[MA-235 AUDIT] Mapping "default" to shield');
    return HEADER_ICON_MAP[DEFAULT_HEADER_ICON];
  }

  // Return mapped component or fallback to shield
  const component = HEADER_ICON_MAP[iconType] || HEADER_ICON_MAP[DEFAULT_HEADER_ICON];
  console.log('[MA-235 AUDIT] Icon component:', {
    'iconType': iconType,
    'component_name': component?.name || component?.displayName || 'unknown',
    'fallback_used': !HEADER_ICON_MAP[iconType],
  });
  
  return component;
};

/**
 * Get icon type string (for logging/debugging)
 * MA-234: STRICT CANONICAL READ
 * 
 * @param {Object} header - Header object from funnel data
 * @returns {string} Icon type token
 */
export const getHeaderIconType = (header) => {
  const iconType = header?.icon_type ?? DEFAULT_HEADER_ICON;
  return iconType === "default" ? DEFAULT_HEADER_ICON : iconType;
};
